package com.appsyde.shared.app.presentation.team

import com.appsyde.shared.core.presentation.BaseViewModel
import com.appsyde.shared.modules.team.domain.model.CreateInviteResult
import com.appsyde.shared.modules.team.domain.model.TeamInvite
import com.appsyde.shared.modules.team.domain.model.TeamUser
import com.appsyde.shared.modules.team.domain.usecase.CreateInviteUseCase
import com.appsyde.shared.modules.team.domain.usecase.DisableUserUseCase
import com.appsyde.shared.modules.team.domain.usecase.GetTeamInvitesUseCase
import com.appsyde.shared.modules.team.domain.usecase.GetTeamUsersUseCase
import com.appsyde.shared.modules.team.domain.usecase.RevokeInviteUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class TeamViewModel(
    private val getTeamUsersUseCase: GetTeamUsersUseCase,
    private val getTeamInvitesUseCase: GetTeamInvitesUseCase,
    private val createInviteUseCase: CreateInviteUseCase,
    private val revokeInviteUseCase: RevokeInviteUseCase,
    private val disableUserUseCase: DisableUserUseCase
) : BaseViewModel() {

    data class State(
        val users: List<TeamUser> = emptyList(),
        val invites: List<TeamInvite> = emptyList(),
        val isLoading: Boolean = false,
        val createInviteLoading: Boolean = false,
        val createInviteMessage: String? = null,
        val createInviteError: Boolean = false
    )

    private val _state = MutableStateFlow(State())
    val state: StateFlow<State> = _state.asStateFlow()

    fun load() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true) }
            val users = getTeamUsersUseCase()
            val invites = getTeamInvitesUseCase()
            _state.update {
                it.copy(users = users, invites = invites, isLoading = false)
            }
        }
    }

    fun createInvite(email: String) {
        val trimmed = email.trim().lowercase()
        if (trimmed.isBlank()) return
        viewModelScope.launch {
            _state.update {
                it.copy(createInviteLoading = true, createInviteMessage = null, createInviteError = false)
            }
            val result = createInviteUseCase(trimmed)
            when (result) {
                is CreateInviteResult.Ok -> {
                    _state.update {
                        it.copy(
                            createInviteLoading = false,
                            createInviteMessage = "Invitation envoyée",
                            createInviteError = false
                        )
                    }
                    load()
                }
                is CreateInviteResult.AlreadyMember -> _state.update {
                    it.copy(
                        createInviteLoading = false,
                        createInviteMessage = "Cet utilisateur est déjà membre",
                        createInviteError = true
                    )
                }
                is CreateInviteResult.CannotInviteOwner -> _state.update {
                    it.copy(
                        createInviteLoading = false,
                        createInviteMessage = "Impossible d'inviter le propriétaire du compte",
                        createInviteError = true
                    )
                }
                is CreateInviteResult.InvalidEmail -> _state.update {
                    it.copy(
                        createInviteLoading = false,
                        createInviteMessage = "Adresse email invalide",
                        createInviteError = true
                    )
                }
                is CreateInviteResult.Error -> _state.update {
                    it.copy(
                        createInviteLoading = false,
                        createInviteMessage = result.message ?: "Erreur lors de l'envoi",
                        createInviteError = true
                    )
                }
            }
        }
    }

    fun clearCreateInviteMessage() {
        _state.update { it.copy(createInviteMessage = null, createInviteError = false) }
    }

    fun revokeInvite(inviteId: String) {
        viewModelScope.launch {
            if (revokeInviteUseCase(inviteId)) load()
        }
    }

    fun disableUser(userId: String) {
        viewModelScope.launch {
            if (disableUserUseCase(userId)) load()
        }
    }
}
